/**
 * ALMACENES MÓVILES / FURGONETAS (F13.4)
 *
 *   /inventario/almacenes-moviles        gestión: furgoneta, responsable, estado, stock, último recuento
 *   /inventario/almacenes-moviles/:id    ficha: stock, técnico (asignar / cambiar con recuento / urgencia), histórico
 *   /mi-furgoneta                        el técnico: SU stock, alertas de lote, escáner para localizar
 *
 * Una furgoneta es un almacén MOVIL con su stock normal: aquí no se mueve nada. Cargar o devolver material es un
 * traslado normal; consumir, un consumo de PT. Cambiar de técnico no mueve stock (con stock, exige recuento).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  api, type AlmacenMovilDetalle, type AlmacenMovilResumen, type LineaStockMovil, type MiAlmacenMovil, type TecnicoAsignable,
} from '../api.ts';
import { Error as ErrorCaja, fecha, fechaDeInstante, fechaHora, Modal, Panel, useDatos, Vacio } from '../componentes/base.tsx';
import { Buscador } from '../componentes/buscador.tsx';
import { Escaner } from '../componentes/escaner.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import type { Lectura } from '../escaner/tipos.ts';
import { erroresDe } from './stock.tsx';
import { codigoAlmacen } from '../componentes/codigo-articulo.tsx';

const BASE = '/api/inventario/almacenes-moviles';

function Alertas({ a }: { a: Pick<AlmacenMovilResumen, 'lotesBloqueados' | 'lotesCaducados'> }): ReactNode {
  return (
    <>
      {a.lotesBloqueados > 0 && <> <span className="etiqueta peligro">{a.lotesBloqueados} lote{a.lotesBloqueados === 1 ? '' : 's'} bloqueado{a.lotesBloqueados === 1 ? '' : 's'}</span></>}
      {a.lotesCaducados > 0 && <> <span className="etiqueta aviso">{a.lotesCaducados} caducado{a.lotesCaducados === 1 ? '' : 's'}</span></>}
    </>
  );
}

// ================================================================ gestión: listado
export function PaginaAlmacenesMoviles(): ReactNode {
  const [texto, setTexto] = useState('');
  const lista = useDatos(() => api.get<{ filas: AlmacenMovilResumen[] }>(`${BASE}?${new URLSearchParams({ texto }).toString()}`), [texto]);
  return (
    <Shell dominio="almacenes" migas={[{ texto: 'Inventario' }, { texto: 'Almacenes', a: '/inventario/almacenes' }, { texto: 'Almacenes móviles' }]} titulo="Almacenes móviles"
      subtitulo="Furgonetas y su técnico responsable. Su stock es el de cualquier almacén: se carga y se devuelve con traslados.">
      <div className="panel">
        <div className="panel-cuerpo">
          <label className="campo" style={{ maxWidth: 360 }}><span>Buscar</span>
            <input value={texto} onChange={(ev) => setTexto(ev.target.value)} placeholder="Furgoneta o técnico" /></label>
        </div>
        <Panel estado={lista}>
          {(d) => (d.filas.length === 0 ? <Vacio titulo="No hay almacenes móviles">Crea un almacén de tipo «Móvil» en Almacenes.</Vacio> : (
            <div className="tabla-desliza">
              <table aria-label="Almacenes móviles" className="tabla-tarjetas">
                <thead><tr><th>Furgoneta</th><th>Responsable</th><th>Estado</th><th>Stock</th><th>Último recuento</th></tr></thead>
                <tbody>
                  {d.filas.map((a) => (
                    <tr key={a.id}>
                      <td className="celda-titulo"><Link to={`/inventario/almacenes-moviles/${a.id}`}>{codigoAlmacen(a)}</Link> <span className="suave">{a.descripcion}</span>
                        {a.centro !== null && <div className="pequena suave">{a.centro.descripcion}</div>}</td>
                      <td data-label="Responsable">{a.responsable === null ? <span className="suave">Sin técnico</span>
                        : <>{a.responsable.nombre} <span className="pequena suave">desde {fechaDeInstante(a.responsable.desde)}</span></>}</td>
                      <td data-label="Estado">{a.activo ? <span className="etiqueta exito">Activa</span> : <span className="etiqueta neutra">Inactiva</span>}
                        {a.recuentoAbierto !== null && <> <span className="etiqueta info">Recuento {a.recuentoAbierto.codigo}</span></>}</td>
                      <td data-label="Stock">{a.referencias === 0 ? <span className="suave">Vacía</span> : `${String(a.referencias)} referencia${a.referencias === 1 ? '' : 's'}`}<Alertas a={a} /></td>
                      <td data-label="Último recuento">{a.ultimoRecuento === null ? <span className="suave">Nunca</span>
                        : <Link to={`/inventario/inventarios/${a.ultimoRecuento.id}`}>{a.ultimoRecuento.codigo}</Link>}
                        {a.ultimoRecuento !== null && <span className="pequena suave"> · {fechaDeInstante(a.ultimoRecuento.cerradoEn)}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Panel>
      </div>
    </Shell>
  );
}

// ================================================================ stock (compartido)
function StockMovil({ lineas, resaltado }: { lineas: LineaStockMovil[]; resaltado?: string | null }): ReactNode {
  if (lineas.length === 0) return <Vacio titulo="Sin stock" />;
  return (
    <div className="tabla-desliza">
      <table aria-label="Stock de la furgoneta" className="tabla-tarjetas">
        <thead><tr><th>Artículo</th><th>Lote</th><th>Caducidad</th><th className="num">Cantidad</th></tr></thead>
        <tbody>
          {lineas.map((l) => {
            const k = `${l.articulo.id}:${l.lote?.id ?? ''}`;
            return (
              <tr key={k} aria-current={resaltado === k ? 'true' : undefined} style={resaltado === k ? { outline: '3px solid var(--color-principal, #0f6ea8)' } : undefined}>
                <td className="celda-titulo"><strong>{l.articulo.codigoNegocio}</strong> {l.articulo.descripcion}
                  {l.series.length > 0 && <div className="pequena mono" style={{ overflowWrap: 'anywhere' }}>Series: {l.series.join(', ')}</div>}</td>
                <td data-label="Lote">{l.lote === null ? <span className="suave">—</span> : <>{l.lote.codigo}
                  {l.lote.estado === 'BLOQUEADO' && <> <span className="etiqueta peligro">Bloqueado: no se consume</span></>}</>}</td>
                <td data-label="Caducidad">{l.lote?.fechaCaducidad == null ? <span className="suave">—</span>
                  : <>{fecha(l.lote.fechaCaducidad)}{l.lote.caducado && <> <span className="etiqueta aviso">Caducado</span></>}</>}</td>
                <td className="num" data-label="Cantidad"><strong>{l.cantidad}</strong> {l.unidad.codigo}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ================================================================ gestión: ficha
export function PaginaAlmacenMovil(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const ficha = useDatos(() => api.get<AlmacenMovilDetalle>(`${BASE}/${encodeURIComponent(id)}`), [id]);
  const stock = useDatos(() => api.get<{ filas: LineaStockMovil[] }>(`${BASE}/${encodeURIComponent(id)}/stock`), [id]);
  const [dialogo, setDialogo] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const d = ficha.datos;
  const migas = [{ texto: 'Inventario' }, { texto: 'Almacenes móviles', a: '/inventario/almacenes-moviles' }, { texto: d === null ? '…' : codigoAlmacen(d) }];
  const asigna = puede('almacenes_moviles:asignar');
  const recuenta = asigna && puede('inventarios:crear') && puede('inventarios:gestionar');

  async function iniciarRecuento(): Promise<void> {
    setError(null);
    setOcupado(true);
    try {
      await api.post(`${BASE}/${id}/recuento`, {});
      ficha.recargar();
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Shell dominio="almacenes" migas={migas} titulo={d === null ? 'Almacén móvil' : `${codigoAlmacen(d)} · ${d.descripcion}`}
      subtitulo={d === null ? undefined : d.responsable === null ? 'Sin técnico responsable' : `Responsable: ${d.responsable.nombre} desde ${fechaDeInstante(d.responsable.desde)}`}>
      {error !== null && <ErrorCaja error={error} />}
      <Panel estado={ficha}>
        {(x) => (
          <>
            <div className="panel">
              <div className="panel-cuerpo fila" style={{ gap: 8, flexWrap: 'wrap' }}>
                {asigna && x.activo && <button type="button" className="principal" style={{ minHeight: 44 }} onClick={() => setDialogo(true)}>
                  {x.responsable === null ? 'Asignar técnico' : 'Cambiar técnico'}</button>}
                {recuenta && x.activo && x.conStock && x.recuentoAbierto === null && (
                  <button type="button" style={{ minHeight: 44 }} disabled={ocupado} onClick={() => void iniciarRecuento()}>Iniciar recuento de traspaso</button>
                )}
                {x.recuentoAbierto !== null && <Link className="boton" to={`/inventario/inventarios/${x.recuentoAbierto.id}`}>Recuento {x.recuentoAbierto.codigo} ({x.recuentoAbierto.estado.toLowerCase().replace('_', ' ')})</Link>}
                {puede('operaciones_stock:crear') && x.activo && <>
                  <Link className="boton" to={`/inventario/operaciones/nueva?${new URLSearchParams({ tipo: 'TRASLADO', destino: x.id, destinoCodigo: codigoAlmacen(x) }).toString()}`}>Cargar (traslado a la furgoneta)</Link>
                  <Link className="boton" to={`/inventario/operaciones/nueva?${new URLSearchParams({ tipo: 'TRASLADO', origen: x.id, origenCodigo: codigoAlmacen(x) }).toString()}`}>Devolver (traslado desde la furgoneta)</Link>
                </>}
              </div>
              <div className="panel-cuerpo pequena suave">
                Stock: {x.referencias} referencia{x.referencias === 1 ? '' : 's'}<Alertas a={x} /> · Último recuento: {x.ultimoRecuento === null ? 'nunca'
                  : <Link to={`/inventario/inventarios/${x.ultimoRecuento.id}`}>{x.ultimoRecuento.codigo} ({fechaDeInstante(x.ultimoRecuento.cerradoEn)})</Link>}
              </div>
            </div>
            <div className="panel">
              <div className="panel-cabecera"><h2>Stock</h2></div>
              <div className="panel-cuerpo"><Panel estado={stock}>{(s) => <StockMovil lineas={s.filas} />}</Panel></div>
            </div>
            <div className="panel">
              <div className="panel-cabecera"><h2>Histórico de responsables</h2></div>
              <div className="panel-cuerpo">
                {x.historial.length === 0 ? <Vacio titulo="Nunca ha tenido técnico" /> : (
                  <ol className="lista-historial" aria-label="Histórico de responsables">
                    {x.historial.map((h) => (
                      <li key={h.id}>
                        <strong>{h.tecnico.nombre}</strong> · {fechaHora(h.desde)} → {h.hasta === null ? <span className="etiqueta exito">vigente</span> : fechaHora(h.hasta)}
                        <div className="pequena">Asignado por {h.asignadoPor}: {h.motivo}
                          {h.sinRecuento && <> <span className="etiqueta peligro">Sin recuento (urgencia)</span></>}
                          {h.inventario !== null && <> · recuento <Link to={`/inventario/inventarios/${h.inventario.id}`}>{h.inventario.codigo}</Link></>}</div>
                        {h.hasta !== null && <div className="pequena suave">Cerrado por {h.cerradoPor ?? '—'}{h.motivoCierre !== null ? `: ${h.motivoCierre}` : ''}</div>}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
            {dialogo && <DialogoResponsable a={x} cerrar={() => setDialogo(false)} alHacer={() => { setDialogo(false); ficha.recargar(); stock.recargar(); }} />}
          </>
        )}
      </Panel>
    </Shell>
  );
}

const buscarTecnicos = async (texto: string): Promise<TecnicoAsignable[]> =>
  (await api.get<{ filas: TecnicoAsignable[] }>(`${BASE}/tecnicos?${new URLSearchParams({ texto }).toString()}`)).filas;

/**
 * Asignar / cambiar / liberar. Con stock: el traspaso usa el último recuento cerrado (la base comprueba que sea de la
 * asignación actual y sin movimientos después); en una urgencia, el ADMIN puede forzarlo con motivo.
 */
function DialogoResponsable({ a, cerrar, alHacer }: { a: AlmacenMovilDetalle; cerrar: () => void; alHacer: () => void }): ReactNode {
  const { puede } = useSesion();
  const [tecnico, setTecnico] = useState<TecnicoAsignable | null>(null);
  const [liberar, setLiberar] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [forzar, setForzar] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const campos = erroresDe(error);
  const recuento = a.conStock && !forzar ? a.ultimoRecuento : null;
  const necesitaRecuento = a.conStock && !forzar && a.ultimoRecuento === null;
  const listo = (liberar || tecnico !== null) && motivo.trim().length >= (forzar ? 10 : 3) && !necesitaRecuento;

  async function hacer(): Promise<void> {
    setError(null);
    setEnviando(true);
    try {
      await api.post(`${BASE}/${a.id}/responsable`, {
        idOperario: liberar ? null : tecnico?.id ?? null, motivo: motivo.trim(),
        ...(recuento !== null ? { idInventario: recuento.id } : {}), ...(forzar ? { forzar: true } : {}),
      });
      alHacer();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  return (
    <Modal titulo={a.responsable === null ? `Asignar técnico a ${codigoAlmacen(a)}` : `Cambiar el técnico de ${codigoAlmacen(a)}`} cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Cancelar</button>
        <button type="button" className={forzar ? 'peligro' : 'principal'} disabled={!listo || enviando} onClick={() => void hacer()}>
          {forzar ? 'Cambiar sin recuento' : 'Confirmar'}</button></>}>
      {error !== null && <ErrorCaja error={error} />}
      <p className="pequena">Cambiar de técnico <strong>no mueve stock</strong>: el material sigue en {codigoAlmacen(a)}. El técnico anterior deja de verla y el nuevo la ve al momento.</p>
      {a.responsable !== null && (
        <label className="fila" style={{ gap: 6, marginBottom: 12 }}><input type="checkbox" checked={liberar} onChange={(ev) => { setLiberar(ev.target.checked); setTecnico(null); }} />
          Dejar la furgoneta sin técnico (taller, reserva…)</label>
      )}
      {!liberar && (
        <Buscador<TecnicoAsignable> etiqueta="Técnico" valor={tecnico} alElegir={setTecnico} buscar={buscarTecnicos} clave={(t) => t.id}
          texto={(t) => `${t.nombre} (${t.codigo})`} detalle={(t) => (t.almacenActual === null ? 'Sin furgoneta' : `Ya tiene ${codigoAlmacen(t.almacenActual)}`)}
          error={campos['idOperario']} sinResultados="Ningún técnico de mantenimiento activo." />
      )}
      {tecnico?.almacenActual != null && tecnico.almacenActual.id !== a.id && (
        <p className="nota aviso">{tecnico.nombre} ya es responsable de {codigoAlmacen(tecnico.almacenActual)}: libérala antes (un técnico, una furgoneta).</p>
      )}
      {a.conStock && !forzar && (a.ultimoRecuento === null
        ? <p className="nota aviso">La furgoneta tiene stock: el cambio exige un <strong>recuento cerrado</strong>. Inicia el recuento de traspaso, que lo cuente el técnico y valida las diferencias.</p>
        : <p className="nota info">Se usará el recuento <Link to={`/inventario/inventarios/${a.ultimoRecuento.id}`}>{a.ultimoRecuento.codigo}</Link> ({fechaDeInstante(a.ultimoRecuento.cerradoEn)}). Si ha habido movimientos después, NeatOS pedirá uno nuevo.</p>)}
      <div className="campo"><label htmlFor="amv-motivo">Motivo{forzar ? ' (urgencia, 10 caracteres o más)' : ''}</label>
        <textarea id="amv-motivo" rows={2} maxLength={500} value={motivo} onChange={(ev) => setMotivo(ev.target.value)}
          placeholder={forzar ? 'Por ejemplo: vehículo averiado, baja inmediata del técnico' : 'Por ejemplo: cambio de ruta'} />
        {campos['motivo'] !== undefined && <div className="error-campo">{campos['motivo']}</div>}</div>
      {a.conStock && puede('almacenes_moviles:forzar') && (
        <details open={forzar}>
          <summary className="pequena">Urgencia (sólo administración)</summary>
          <label className="fila" style={{ gap: 6 }}><input type="checkbox" checked={forzar} onChange={(ev) => setForzar(ev.target.checked)} />
            Cambiar sin recuento. Queda registrado con tu usuario, la fecha y el motivo.</label>
        </details>
      )}
    </Modal>
  );
}

// ================================================================ técnico: «Mi furgoneta»
export function PaginaMiFurgoneta(): ReactNode {
  const mi = useDatos(() => api.get<MiAlmacenMovil>('/api/inventario/mi-almacen-movil'), []);
  const [texto, setTexto] = useState('');
  const [resaltado, setResaltado] = useState<string | null>(null);
  return (
    <Shell migas={[{ texto: 'Mi furgoneta' }]} titulo="Mi furgoneta">
      <Panel estado={mi}>
        {(d) => (d.almacen === null ? (
          <div className="panel"><Vacio titulo="No tienes ninguna furgoneta asignada">Si deberías tenerla, avisa a tu responsable.</Vacio></div>
        ) : <ContenidoMiFurgoneta d={d as MiAlmacenMovil & { almacen: NonNullable<MiAlmacenMovil['almacen']> }} texto={texto} setTexto={setTexto}
          resaltado={resaltado} setResaltado={setResaltado} />)}
      </Panel>
    </Shell>
  );
}

function ContenidoMiFurgoneta({ d, texto, setTexto, resaltado, setResaltado }: {
  d: MiAlmacenMovil & { almacen: NonNullable<MiAlmacenMovil['almacen']> }; texto: string; setTexto: (t: string) => void;
  resaltado: string | null; setResaltado: (k: string | null) => void;
}): ReactNode {
  const unidades = d.stock.reduce((s, l) => s + Number(l.cantidad), 0);
  const referencias = new Set(d.stock.map((l) => l.articulo.id)).size;
  const bloqueados = d.stock.filter((l) => l.lote?.estado === 'BLOQUEADO').length;
  const caducados = d.stock.filter((l) => l.lote?.caducado === true).length;
  const visibles = useMemo(() => {
    const t = texto.trim().toLowerCase();
    if (t === '') return d.stock;
    return d.stock.filter((l) => [l.articulo.codigo, l.articulo.codigoNegocio, l.articulo.descripcion, l.lote?.codigo ?? '', ...l.series]
      .some((x) => x.toLowerCase().includes(t)));
  }, [d.stock, texto]);

  /** Escanear aquí sólo LOCALIZA: no cambia nada. */
  function localizar(l: Lectura): string | null {
    const r = l.recurso;
    if (r.articulo === null) return 'La lectura no identifica un artículo.';
    const linea = d.stock.find((x) => x.articulo.id === r.articulo!.id && (r.lote === null || x.lote?.id === r.lote.id));
    if (linea === undefined) return `${r.articulo.codigoNegocio}${r.lote !== null ? ` (lote ${r.lote.codigo})` : ''} no está en tu furgoneta.`;
    setTexto('');
    setResaltado(`${linea.articulo.id}:${linea.lote?.id ?? ''}`);
    return null;
  }

  return (
    <>
      <div className="panel">
        <div className="panel-cuerpo">
          <p style={{ margin: 0 }}><strong>{codigoAlmacen(d.almacen)}</strong> · {d.almacen.descripcion} <span className="pequena suave">desde {fechaDeInstante(d.almacen.desde)}</span></p>
          <div className="fila" style={{ gap: 16, flexWrap: 'wrap', marginTop: 8 }} aria-label="Resumen">
            <div><div className="pequena suave">Referencias</div><strong style={{ fontSize: '1.4em' }}>{referencias}</strong></div>
            <div><div className="pequena suave">Unidades</div><strong style={{ fontSize: '1.4em' }}>{unidades.toLocaleString('es-ES')}</strong></div>
            <div><div className="pequena suave">Alertas de lote</div><strong style={{ fontSize: '1.4em' }}>{bloqueados + caducados}</strong>
              {bloqueados > 0 && <> <span className="etiqueta peligro">{bloqueados} bloqueado{bloqueados === 1 ? '' : 's'}</span></>}
              {caducados > 0 && <> <span className="etiqueta aviso">{caducados} caducado{caducados === 1 ? '' : 's'}</span></>}</div>
          </div>
          {d.recuentoAbierto !== null && d.recuentoAbierto.estado === 'EN_CONTEO' && (
            <p className="nota info" style={{ marginTop: 12 }}>Tienes un recuento pendiente ({d.recuentoAbierto.codigo}).{' '}
              <Link className="boton principal" to={`/inventario/inventarios/${d.recuentoAbierto.id}`}>Contar ahora</Link></p>
          )}
          <p className="pequena suave" style={{ marginBottom: 0 }}>Para consumir material, ábrelo desde el parte: «Material» → escanear. Para devolverlo al almacén, avisa a tu responsable (es un traslado).</p>
        </div>
      </div>
      <Escaner titulo="Buscar en mi furgoneta" contexto={{ uso: 'CONSULTA', idAlmacen: d.almacen.id }} alLeer={localizar} textoExito="Localizado: " />
      <div className="panel">
        <div className="panel-cuerpo">
          <label className="campo" style={{ maxWidth: 360 }}><span>Filtrar</span>
            <input value={texto} onChange={(ev) => { setTexto(ev.target.value); setResaltado(null); }} placeholder="Código, descripción, lote o serie" /></label>
        </div>
        <StockMovil lineas={visibles} resaltado={resaltado} />
      </div>
    </>
  );
}
